import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Checkbox,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Divider,
} from '@mui/material';
import axiosInstance from '../utils/axios';
import { UserRequest } from '../types/types';
import { t } from '../utils/translations';

interface AcceptanceItem {
  id: number;
  product_id: number;
  product_name: string;
  category_name: string;
  quantity: number;
  is_accepted: boolean;
  acceptance_comment: string;
}

const AcceptRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState<UserRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [acceptanceItems, setAcceptanceItems] = useState<AcceptanceItem[]>([]);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await axiosInstance.get<UserRequest>(`/api/v1/user/requests/${id}/`);
        setRequest(response.data);
        
        // Initialize acceptance items
        const items = response.data.items.map(item => {
          return {
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name,
            category_name: item.category_name,
            quantity: item.quantity,
            is_accepted: true,
            acceptance_comment: ''
          };
        });
        setAcceptanceItems(items);
      } catch (error) {
        console.error('Error fetching request:', error);
        navigate('/requests');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequest();
  }, [id, navigate]);

  const handleAcceptanceChange = (productId: number, accepted: boolean) => {
    
    setAcceptanceItems(prevItems => {
      const newItems = prevItems.map(item => {
        if (item.product_id === productId) {
          return {
            ...item,
            is_accepted: accepted,
            acceptance_comment: accepted ? '' : item.acceptance_comment
          };
        }
        return item;
      });
      
      return newItems;
    });
  };

  const handleCommentChange = (productId: number, comment: string) => {
    setAcceptanceItems(items =>
      items.map(item =>
        item.product_id === productId
          ? { ...item, acceptance_comment: comment }
          : item
      )
    );
  };

  const handleSubmit = async () => {
    try {
      const acceptanceData = {
        items: acceptanceItems.map(item => ({
          product_id: item.product_id,
          is_accepted: item.is_accepted,
          acceptance_comment: item.acceptance_comment
        }))
      };

      await axiosInstance.post(`/api/v1/requests/${id}/accept/`, acceptanceData);
      navigate('/requests');
    } catch (error) {
      console.error('Error accepting request:', error);
    }
  };

  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!request) {
    return (
      <Container>
        <Typography variant="h6" color="error">Request not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('Accept Request')} #{id}
      </Typography>
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : request ? (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('Created at')}: {request.created_at_formatted}
            </Typography>
            {request.comment && (
              <Typography variant="body1">
                {t('Comment')}: {request.comment}
              </Typography>
            )}
            {request.shipping_address && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                {t('Shipping Address')}: {request.shipping_address}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h5" gutterBottom>
            {t('Items')}
          </Typography>

          {Object.entries(
            acceptanceItems.reduce<Record<string, AcceptanceItem[]>>((acc, item) => {
              if (!acc[item.category_name]) {
                acc[item.category_name] = [];
              }
              acc[item.category_name].push(item);
              return acc;
            }, {})
          ).map(([category, items]) => (
            <Box key={`category-${category}`} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  bgcolor: 'grey.100',
                  p: 1,
                  mb: 2,
                  fontWeight: 'bold'
                }}
              >
                {category}
              </Typography>

              {items.map((item) => (
                <Card key={`item-${item.id}-${item.product_id}`} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {item.product_name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {t('Quantity')}: {item.quantity}
                        </Typography>
                      </Box>
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.is_accepted}
                            onChange={(e) => handleAcceptanceChange(item.product_id, e.target.checked)}
                          />
                        }
                        label={t('Accept this item')}
                      />

                      {!item.is_accepted && (
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Reason for rejection')}
                          value={item.acceptance_comment}
                          onChange={(e) => handleCommentChange(item.product_id, e.target.value)}
                          required
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 2, mt: 4, mb: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/requests')}
              sx={{ 
                borderRadius: 2,
                borderColor: 'rgba(236, 36, 143, 0.5)',
                color: '#ec248f',
                '&:hover': {
                  backgroundColor: 'rgba(236, 36, 143, 0.04)',
                  borderColor: '#ec248f'
                }
              }}
            >
              {t('Requests')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={acceptanceItems.some(item => !item.is_accepted && !item.acceptance_comment)}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
                boxShadow: '0 4px 10px rgba(236, 36, 143, 0.25)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(236, 36, 143, 0.35)',
                }
              }}
            >
              {t('Accept Request')}
            </Button>
          </Box>
        </>
      ) : (
        <Typography color="error">
          {t('Request not found')}
        </Typography>
      )}
    </Container>
  );
};

export default AcceptRequest; 